import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  Globe, 
  FileText, 
  Eye, 
  Edit, 
  Trash2, 
  Plus,
  Save,
  ExternalLink,
  Home,
  Info,
  Phone,
  Briefcase,
  Loader2,
  GripVertical
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

const AdminPagesPage = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewPage, setShowNewPage] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [newPage, setNewPage] = useState({ title: '', slug: '' });
  const [saving, setSaving] = useState(false);

  const iconMap = {
    'accueil': Home,
    'a-propos': Info,
    'services': Briefcase,
    'contact': Phone,
  };

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      setLoading(true);
      const res = await api.get('/website/pages');
      setPages(res.data || []);
    } catch (error) {
      console.error('Error loading pages:', error);
      toast.error('Erreur lors du chargement des pages');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublish = async (page) => {
    try {
      const newStatus = page.status === 'published' ? 'draft' : 'published';
      await api.put(`/website/pages/${page.id}`, { status: newStatus });
      setPages(pages.map(p => 
        p.id === page.id ? { ...p, status: newStatus } : p
      ));
      toast.success(`Page ${newStatus === 'published' ? 'publiée' : 'dépubliée'}`);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleCreatePage = async () => {
    if (!newPage.title || !newPage.slug) {
      toast.error('Titre et URL requis');
      return;
    }
    
    setSaving(true);
    try {
      const res = await api.post('/website/pages', {
        title: newPage.title,
        slug: newPage.slug.toLowerCase().replace(/\s+/g, '-')
      });
      setPages([...pages, res.data]);
      setShowNewPage(false);
      setNewPage({ title: '', slug: '' });
      toast.success('Page créée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePage = async () => {
    if (!editingPage) return;
    
    setSaving(true);
    try {
      await api.put(`/website/pages/${editingPage.id}`, {
        title: editingPage.title,
        slug: editingPage.slug,
        seo_title: editingPage.seo_title,
        seo_description: editingPage.seo_description,
      });
      setPages(pages.map(p => p.id === editingPage.id ? editingPage : p));
      setEditingPage(null);
      toast.success('Page mise à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePage = async (pageId) => {
    if (!window.confirm('Supprimer cette page ?')) return;
    
    try {
      await api.delete(`/website/pages/${pageId}`);
      setPages(pages.filter(p => p.id !== pageId));
      toast.success('Page supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a365d]">Pages du site</h1>
          <p className="text-[#64748b]">Gérez les pages de votre site vitrine</p>
        </div>
        <Button onClick={() => setShowNewPage(true)} className="bg-amber-600 hover:bg-amber-700">
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle page
        </Button>
      </div>

      {/* Pages List */}
      <div className="grid gap-4">
        {pages.map((page) => {
          const Icon = iconMap[page.slug] || FileText;
          return (
            <Card key={page.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="cursor-grab text-slate-400">
                      <GripVertical className="w-4 h-4" />
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-[#1a365d]">{page.title}</h3>
                        {page.is_home && (
                          <Badge variant="outline" className="text-xs">Page d'accueil</Badge>
                        )}
                      </div>
                      <p className="text-xs text-[#94a3b8] mt-1">/{page.slug}</p>
                      {page.seo_description && (
                        <p className="text-sm text-[#64748b] mt-1 line-clamp-1">{page.seo_description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={page.status === 'published' ? 'default' : 'secondary'}>
                      {page.status === 'published' ? 'Publié' : 'Brouillon'}
                    </Badge>
                    <Switch 
                      checked={page.status === 'published'} 
                      onCheckedChange={() => handleTogglePublish(page)}
                    />
                    <Button variant="ghost" size="icon" onClick={() => setEditingPage(page)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    {!page.is_home && (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDeletePage(page.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {pages.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <Globe className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucune page créée</p>
            <Button onClick={() => setShowNewPage(true)} variant="outline" className="mt-4">
              <Plus className="w-4 h-4 mr-2" />
              Créer une page
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Info */}
      <Card className="border-amber-200 bg-amber-50/50">
        <CardContent className="p-4">
          <p className="text-sm text-amber-800">
            <strong>💡 Astuce :</strong> Ces pages sont affichées sur votre site vitrine. Modifiez le contenu directement depuis l'éditeur de page.
          </p>
        </CardContent>
      </Card>

      {/* New Page Dialog */}
      <Dialog open={showNewPage} onOpenChange={setShowNewPage}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle page</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Titre de la page</Label>
              <Input 
                value={newPage.title}
                onChange={(e) => setNewPage({...newPage, title: e.target.value})}
                placeholder="Ex: Nos réalisations"
              />
            </div>
            <div className="space-y-2">
              <Label>URL (slug)</Label>
              <Input 
                value={newPage.slug}
                onChange={(e) => setNewPage({...newPage, slug: e.target.value})}
                placeholder="Ex: realisations"
              />
              <p className="text-xs text-slate-500">L'URL sera: /realisations</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewPage(false)}>Annuler</Button>
            <Button onClick={handleCreatePage} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Page Dialog */}
      <Dialog open={!!editingPage} onOpenChange={() => setEditingPage(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifier la page</DialogTitle>
          </DialogHeader>
          {editingPage && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Titre</Label>
                <Input 
                  value={editingPage.title}
                  onChange={(e) => setEditingPage({...editingPage, title: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>URL (slug)</Label>
                <Input 
                  value={editingPage.slug}
                  onChange={(e) => setEditingPage({...editingPage, slug: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Titre SEO</Label>
                <Input 
                  value={editingPage.seo_title || ''}
                  onChange={(e) => setEditingPage({...editingPage, seo_title: e.target.value})}
                  placeholder="Titre optimisé pour le référencement"
                />
              </div>
              <div className="space-y-2">
                <Label>Description SEO</Label>
                <textarea 
                  className="w-full p-2 border rounded-lg text-sm"
                  rows={3}
                  value={editingPage.seo_description || ''}
                  onChange={(e) => setEditingPage({...editingPage, seo_description: e.target.value})}
                  placeholder="Description pour les moteurs de recherche (160 caractères max)"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPage(null)}>Annuler</Button>
            <Button onClick={handleUpdatePage} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPagesPage;
